/**
 * The FBC's view — decision 0007 (Bettina, 2026-11-02).
 *
 * An FBC sees only the Notified-or-later cases assigned to them, at the detail
 * the territory notice shows (AF#s and owner names, zip, client count), and
 * moves each one step at a time — Notified → Acknowledged → Confirmed
 * violation — with a note every time. Notes are dated and timestamped by the
 * server. Bettina reads them on her dashboard.
 */

import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { ImpersonationBanner } from '../components/ImpersonationBanner';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../auth/AuthContext';

interface FbcUpdate { id: number; from_state: string; to_state: string | null; note: string; created_at: string; by: string | null }
interface FbcCase {
  id: number;
  state: string;
  state_label: string;
  servicing_af: string;
  servicing_owners: string | null;
  territory_af: string | null;
  territory_owners: string | null;
  zip: string | null;
  clients: number;
  notified_at: string | null;
  editable: boolean;
  next_state: string | null;
  next_label: string | null;
  updates: FbcUpdate[];
}

const LABEL: Record<string, string> = { NOTIFIED: 'Notified', ACKD: 'Acknowledged', CONFIRMED_VIOLATION: 'Confirmed violation', RESOLVED: 'Resolved' };
const stamp = (d: string) => new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
const errText = (e: unknown, fallback: string) => {
  const body = e instanceof ApiError ? (e.body as { error?: string } | null) : null;
  return body?.error ?? (e instanceof Error ? e.message : fallback);
};

export function FbcHome() {
  const { user, logout } = useAuth();
  const [cases, setCases] = useState<FbcCase[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = () => api<{ cases: FbcCase[] }>('/api/fbc/cases')
    .then((d) => setCases(d.cases)).catch((e) => setError(errText(e, 'Could not load your cases')));
  useEffect(() => { load(); }, []);

  const open = (cases ?? []).filter((c) => c.editable);
  const closed = (cases ?? []).filter((c) => !c.editable);

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ImpersonationBanner />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
        <div style={{ fontWeight: 600 }}>Territory monitoring · FBC updates</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
          <span className="muted">{user?.name ?? user?.email}</span>
          <button className="btn btn-sm btn-ghost" onClick={logout}>Sign out</button>
        </div>
      </div>
      <div className="scroll-y" style={{ flex: 1, padding: 24 }}>
        <div style={{ maxWidth: 900 }}>
          <h1 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 600 }}>Your territory notices</h1>
          <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
            Cases where a notice went to one of your franchise partners as the servicing franchisee. When the partner responds, mark the case
            <b> Acknowledged</b> with a note. If it turns out to be a violation being worked out, mark it <b>Confirmed violation</b> with a note.
            You can also add a note at any time. Every update is dated and shared with AFI.
          </div>
          {error && <div className="card card-pad c-danger" style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>}
          {!cases && !error && <div className="muted" style={{ fontSize: 13 }}>Loading…</div>}
          {cases && cases.length === 0 && <div className="card card-pad muted" style={{ fontSize: 13 }}>No cases are assigned to you right now.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {open.map((c) => <FbcCaseCard key={c.id} c={c} onChanged={load} />)}
          </div>
          {closed.length > 0 && (
            <>
              <h2 style={{ margin: '24px 0 8px', fontSize: 15, fontWeight: 600 }}>Resolved</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {closed.map((c) => <FbcCaseCard key={c.id} c={c} onChanged={load} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FbcCaseCard({ c, onChanged }: { c: FbcCase; onChanged: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (toState?: string) => {
    if (!note.trim()) { setErr('A note is required.'); return; }
    setBusy(true); setErr(null);
    try {
      await api(`/api/fbc/cases/${c.id}/updates`, { method: 'POST', body: { note: note.trim(), ...(toState ? { to_state: toState } : {}) } });
      setNote('');
      onChanged();
    } catch (e) { setErr(errText(e, 'Could not save the update')); } finally { setBusy(false); }
  };

  return (
    <div className="card card-pad">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Case #{c.id} <span className="pill" style={{ height: 20, fontSize: 10.5, marginLeft: 6 }}>{c.state_label}</span></div>
          <div style={{ fontSize: 13, marginTop: 4 }}>
            <b>{c.clients}</b> client serviced by <b>{c.servicing_af}{c.servicing_owners ? `, ${c.servicing_owners}` : ''}</b>,
            {' '}in the Designated Territory of <b>{c.territory_af ?? '—'}{c.territory_owners ? `, ${c.territory_owners}` : ''}</b>, zip <b>{c.zip ?? '—'}</b>
          </div>
          {c.notified_at && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>Notice sent {stamp(c.notified_at)}</div>}
        </div>
      </div>

      {c.updates.length > 0 && (
        <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
          {c.updates.map((u) => (
            <div key={u.id} style={{ fontSize: 12.5, marginBottom: 6 }}>
              <span className="muted">{stamp(u.created_at)} · {u.by ?? 'FBC'}</span>
              {u.to_state && <span style={{ fontWeight: 600 }}> · {LABEL[u.from_state] ?? u.from_state} → {LABEL[u.to_state] ?? u.to_state}</span>}
              <div style={{ whiteSpace: 'pre-wrap' }}>{u.note}</div>
            </div>
          ))}
        </div>
      )}

      {c.editable && (
        <div style={{ marginTop: 10 }}>
          <textarea className="input" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="Note (required) — what you heard from the offices, what's agreed, next steps"
                    style={{ width: '100%', resize: 'vertical' }} />
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>Please don't include client names — use the case number.</div>
          {err && <div className="c-danger" style={{ fontSize: 12, marginTop: 4 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            {c.next_state && (
              <button className={c.next_state === 'CONFIRMED_VIOLATION' ? 'btn btn-danger btn-sm' : 'btn btn-primary btn-sm'} disabled={busy}
                      onClick={() => submit(c.next_state!)}>
                <Icon name={c.next_state === 'CONFIRMED_VIOLATION' ? 'alert' : 'check'} size={12} /> {busy ? 'Saving…' : `Mark ${c.next_label}`}
              </button>
            )}
            <button className="btn btn-sm" disabled={busy} onClick={() => submit()}><Icon name="plus" size={12} /> Add note only</button>
          </div>
        </div>
      )}
    </div>
  );
}
